'use client';

import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { useTheme } from '@/components/ThemeProvider';
import { useAuth } from '@/contexts/AuthContext';

export default function AuthScreen() {
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const { theme, toggleTheme } = useTheme();
  const { user, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && user) {
      router.replace('/member-dashboard');
    }
  }, [user, loading, router]);

  if (loading || user) {
    return null;
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-accent">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=1200&q=80')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="absolute inset-0 bg-accent/75" />
          <div className="blob-gold absolute top-1/4 left-1/4 w-96 h-96" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={40} />
            <span className="font-display text-2xl font-semibold tracking-wide text-white">RICHÉSSE</span>
          </Link>

          {/* Center content */}
          <div>
            <h1 className="font-display text-hero-lg text-white mb-4">
              Welcome to<br />your Richésse.
            </h1>
            <p className="text-white/70 text-base leading-relaxed max-w-md mb-8">
              A premium lifestyle brand where wellness meets opportunity. Join 48,000+ members building their story.
            </p>

            {/* Social proof */}
            <div className="flex flex-wrap gap-5">
              {[
                { id: 'sp-stores', icon: 'GlobeAltIcon', value: '48K+', label: 'Active Stores' },
                { id: 'sp-products', icon: 'CubeIcon', value: '127', label: 'Products' },
                { id: 'sp-countries', icon: 'MapPinIcon', value: '32', label: 'Countries' },
              ].map(sp => (
                <div key={sp.id} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                  <Icon name={sp.icon as any} size={18} className="text-primary" />
                  <div>
                    <p className="text-sm font-bold text-white tabular-nums">{sp.value}</p>
                    <p className="text-[10px] text-white/60">{sp.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quote */}
          <div className="border-l-2 border-primary pl-4">
            <p className="text-sm text-white/70 italic leading-relaxed">
              "My Richésse store went live the day I signed up. Within a week I had my first customer."
            </p>
            <p className="text-xs text-primary font-medium mt-2">— Maria Santos, Elite Member</p>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col overflow-y-auto">
        {/* Mobile header */}
        <div className="flex items-center justify-between p-6 lg:hidden border-b border-border">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-display text-base font-semibold text-foreground">RICHÉSSE</span>
          </Link>
          <button onClick={toggleTheme} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted">
            <Icon name={theme === 'dark' ? 'SunIcon' : 'MoonIcon'} size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-12 xl:px-16 max-w-md mx-auto w-full">
          {/* Theme toggle — desktop */}
          <div className="hidden lg:flex justify-end mb-6">
            <button onClick={toggleTheme} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors">
              <Icon name={theme === 'dark' ? 'SunIcon' : 'MoonIcon'} size={18} className="text-muted-foreground" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex rounded-xl bg-muted p-1 mb-8">
            {(['login', 'register'] as const).map(t => (
              <button
                key={`auth-tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 capitalize ${
                  tab === t
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t === 'login' ? 'Sign In' : 'Join Richésse'}
              </button>
            ))}
          </div>

          {tab === 'login' ? (
            <LoginForm onSwitchToRegister={() => setTab('register')} />
          ) : (
            <RegisterForm onSwitchToLogin={() => setTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}